/**
 * ERPAddressBlock — Reusable address component backed by GlobalAddressBook
 * Country/State/City use ERPLookupModal popups (cascading).
 * 
 * Props:
 *   addType       — 'BR'|'WH'|'CR'|'SU'|'GL'|'SM' (GlobalAddressBook.Addtype)
 *   recordId      — parent record ID (loads existing address on change)
 *   values        — { Address, Country, CountryId, State, StateId, City, PinCode, Phone, Fax, CSTNo, TINNo, Pan, AddId }
 *   onChange(f,v)  — single field update
 *   onLoadAddress(obj) — batch update when loading from DB
 *   readOnly      — boolean
 *   token         — auth token
 *   extraFields   — optional array: ['Phone','Fax','CSTNo','TINNo'] to control visible extras
 * 
 * RULE: This component is globally reusable. DO NOT duplicate address forms.
 * See EXECUTION_POLICY.md RULE 5 & RULE 24.
 */
import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Search } from 'lucide-react';
import ERPLookupModal from './ERPLookupModal';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL } from '../../lib/api';


const F = memo(({ label, value, onChange, required, disabled, type = 'text', testId }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control">
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled} className="erp-input" data-testid={testId} />
    </div>
  </div>
));

const LookupField = memo(({ label, value, onSearch, required, disabled, testId }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control" style={{ display: 'flex', gap: 4 }}>
      <input value={value || ''} readOnly className="erp-input" style={{ flex: 1 }} data-testid={testId} />
      <button type="button" className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }} onClick={onSearch} disabled={disabled} data-testid={`${testId}-search`}><Search size={12} /></button>
    </div>
  </div>
));

const EMPTY_ADDR = { Address: '', Country: '', CountryId: '', State: '', StateId: '', City: '', PinCode: '', Phone: '', Fax: '', Mobile: '', Email: '', CSTNo: '', TINNo: '', Pan: '', AddId: '' };

const ERPAddressBlock = ({ addType, recordId, values = {}, onChange, onLoadAddress, readOnly = false, token, extraFields }) => {
  const [lookupType, setLookupType] = useState(null);
  const loadedRef = useRef(null);
  const set = (field, value) => onChange && onChange(field, value);

  // Auto-load address from GlobalAddressBook when recordId changes
  useEffect(() => {
    if (!addType || !recordId || !token || recordId === loadedRef.current) return;
    loadedRef.current = recordId;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/address-book/${addType}/${recordId}`, { headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json();
        if (j.success && j.data?.[0]) {
          const a = j.data[0];
          const mapped = {
            Address: a.Add_Address_Street || '', Country: a.Add_Country || '', State: a.Add_State_Province || '',
            City: a.Add_City || '', PinCode: a.add_Zip_postcode || '', Phone: a.Add_Phone_Buss || '',
            Fax: a.Add_Phone_BFax || '', Mobile: a.Add_Mobile_off || '', Email: a.Add_Email || '',
            CSTNo: a.CSTNo || '', Pan: a.Pan || '', AddId: a.addid || ''
          };
          if (onLoadAddress) onLoadAddress(mapped);
        }
      } catch {}
    };
    load();
  }, [addType, recordId, token, onLoadAddress]);

  // Reset loadedRef when recordId clears (new record)
  useEffect(() => {
    if (!recordId) loadedRef.current = null;
  }, [recordId]);

  // Lookup fetch functions for ERPLookupModal
  const fetchCountries = useCallback(async ({ search }) => {
    try {
      const r = await fetch(`${API_URL}/api/address-book/lookup/countries`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      let data = j.data || [];
      if (search) { const s = search.toLowerCase(); data = data.filter(d => d.CountryName?.toLowerCase().includes(s) || d.Countryid?.toLowerCase().includes(s)); }
      return { data, total: data.length };
    } catch { return { data: [], total: 0 }; }
  }, [token]);

  const fetchStates = useCallback(async ({ search }) => {
    try {
      const qs = new URLSearchParams();
      if (values.CountryId) qs.set('countryId', values.CountryId);
      if (search) qs.set('search', search);
      const r = await fetch(`${API_URL}/api/address-book/lookup/states?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      return { data: j.data || [], total: j.total || j.data?.length || 0 };
    } catch { return { data: [], total: 0 }; }
  }, [token, values.CountryId]);

  const fetchCities = useCallback(async ({ search }) => {
    try {
      const qs = new URLSearchParams();
      if (values.StateId) qs.set('stateId', values.StateId);
      if (search) qs.set('search', search);
      const r = await fetch(`${API_URL}/api/address-book/lookup/cities?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      return { data: j.data || [], total: j.total || j.data?.length || 0 };
    } catch { return { data: [], total: 0 }; }
  }, [token, values.StateId]);

  const show = (f) => !extraFields || extraFields.includes(f);

  return (
    <>
      <div className="erp-form-2col">
        <div>
          <F label="Address" value={values.Address} onChange={v => set('Address', v)} required disabled={readOnly} testId="addr-address" />
          <LookupField label="Country" value={values.Country} onSearch={() => setLookupType('country')} required disabled={readOnly} testId="addr-country" />
          <LookupField label="State" value={values.State} onSearch={() => setLookupType('state')} required disabled={readOnly} testId="addr-state" />
          <LookupField label="City" value={values.City} onSearch={() => setLookupType('city')} required disabled={readOnly} testId="addr-city" />
        </div>
        <div>
          <F label="PIN Code" value={values.PinCode} onChange={v => set('PinCode', v)} disabled={readOnly} testId="addr-pin" />
          {show('Phone') && <F label="Phone No." value={values.Phone} onChange={v => set('Phone', v)} disabled={readOnly} testId="addr-phone" />}
          {show('Fax') && <F label="Fax No." value={values.Fax} onChange={v => set('Fax', v)} disabled={readOnly} testId="addr-fax" />}
          {show('CSTNo') && <F label="CST No." value={values.CSTNo} onChange={v => set('CSTNo', v)} disabled={readOnly} testId="addr-cst" />}
          {show('TINNo') && <F label="TIN No." value={values.TINNo} onChange={v => set('TINNo', v)} disabled={readOnly} testId="addr-tin" />}
        </div>
      </div>

      {lookupType === 'country' && (
        <ERPLookupModal
          title="Select Country"
          columns={[{ field: 'Countryid', header: 'Code' }, { field: 'CountryName', header: 'Country' }]}
          fetchData={fetchCountries}
          onSelect={(item) => { set('Country', item.CountryName); set('CountryId', item.Countryid); set('State', ''); set('StateId', ''); set('City', ''); setLookupType(null); }}
          onClose={() => setLookupType(null)}
        />
      )}
      {lookupType === 'state' && (
        <ERPLookupModal
          title="Select State"
          columns={[{ field: 'StateId', header: 'Code' }, { field: 'StateName', header: 'State' }]}
          fetchData={fetchStates}
          onSelect={(item) => { set('State', item.StateName); set('StateId', item.StateId); set('City', ''); setLookupType(null); }}
          onClose={() => setLookupType(null)}
        />
      )}
      {lookupType === 'city' && (
        <ERPLookupModal
          title="Select City"
          columns={[{ field: 'ID', header: 'Code' }, { field: 'Name', header: 'City' }]}
          fetchData={fetchCities}
          onSelect={(item) => { set('City', item.Name); setLookupType(null); }}
          onClose={() => setLookupType(null)}
        />
      )}
    </>
  );
};

export { ERPAddressBlock, EMPTY_ADDR };
export default ERPAddressBlock;
